import { CircleAlert, CirclePlus, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { categoryDef } from '@/lib/constants';
import { formatVND } from '@/lib/format';
import type { ImportRow, ImportRowResult, ImportSummary } from '@/lib/api';

export interface ImportPreviewProps {
  rows: ImportRow[];
  results: ImportRowResult[];
  summary: ImportSummary;
}

function StatusBadge({ status }: { status: 'new' | 'update' | 'error' }) {
  if (status === 'new') {
    return (
      <Badge variant="secondary" className="h-5 gap-0.5 border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
        <CirclePlus className="h-2.5 w-2.5" />
        New
      </Badge>
    );
  }
  if (status === 'update') {
    return (
      <Badge variant="secondary" className="h-5 gap-0.5 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400">
        <RefreshCw className="h-2.5 w-2.5" />
        Update
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="h-5 gap-0.5 border-destructive/30 bg-destructive/10 text-destructive">
      <CircleAlert className="h-2.5 w-2.5" />
      Error
    </Badge>
  );
}

export function ImportPreview({ rows, results, summary }: ImportPreviewProps) {
  // Map result by index for quick lookup
  const byIdx = new Map(results.map((r) => [r.index, r]));

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 p-3 text-sm">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Tổng:</span>
          <span className="font-semibold tabular-nums">{summary.total}</span>
        </div>
        <div className="h-4 w-px bg-border" />
        <div className="flex items-center gap-1.5">
          <CirclePlus className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-muted-foreground">Mới:</span>
          <span className="font-semibold tabular-nums text-emerald-700">{summary.new}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5 text-amber-600" />
          <span className="text-muted-foreground">Update:</span>
          <span className="font-semibold tabular-nums text-amber-700">{summary.update}</span>
        </div>
        {summary.errors > 0 && (
          <div className="flex items-center gap-1.5">
            <CircleAlert className="h-3.5 w-3.5 text-destructive" />
            <span className="text-muted-foreground">Lỗi:</span>
            <span className="font-semibold tabular-nums text-destructive">{summary.errors}</span>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-16">Trạng thái</TableHead>
              <TableHead className="w-44">SKU</TableHead>
              <TableHead className="min-w-[260px]">Sản phẩm</TableHead>
              <TableHead className="w-24">Hãng</TableHead>
              <TableHead className="w-28">Phân khúc</TableHead>
              <TableHead className="w-16 text-right">Size</TableHead>
              <TableHead className="w-16 text-center">PG</TableHead>
              <TableHead className="w-16 text-right">Hz</TableHead>
              <TableHead className="w-28 text-right">Giá CT</TableHead>
              <TableHead className="w-28 text-right">Giá Web</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => {
              const result = byIdx.get(i);
              const status = result?.status ?? 'error';
              const cat = categoryDef(row.category);
              return (
                <TableRow
                  key={`${row.id}-${i}`}
                  className={cn(
                    status === 'new' && 'bg-emerald-50/40',
                    status === 'update' && 'bg-amber-50/40',
                    status === 'error' && 'bg-destructive/5'
                  )}
                >
                  <TableCell>
                    <StatusBadge status={status} />
                  </TableCell>
                  <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">
                    {row.id}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="line-clamp-1" title={row.name}>
                      {row.name}
                    </div>
                    {result?.error && (
                      <div className="mt-1 text-[11px] text-destructive">{result.error}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm font-medium">{row.brand}</TableCell>
                  <TableCell className="text-xs">
                    {cat ? (
                      <div className="flex items-center gap-1">
                        <cat.icon className={cn('h-3 w-3', cat.colorClass)} />
                        <span>{cat.label}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{row.category}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {row.size ? `${row.size}"` : '—'}
                  </TableCell>
                  <TableCell className="text-center text-xs">{row.resolution ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {row.refresh_rate ?? '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {formatVND(row.price_ct ?? null)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs text-primary">
                    {formatVND(row.price_web ?? null)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
